/**
 * An output in the chat, as the elevated Workshop shows it (stage 2a): the kind
 * tag only when the data carries a real type, the version, one Barlow title, and
 * Open beside. The whole card is the open target; the button's hit area covers it.
 *
 * Open beside puts the output in the split desk's 600px pane (stage 2b); below
 * 1100px the pane is a sheet and the card says Open.
 */
import type { ReactNode } from "react";
import { ArrowUpRight, PanelRight } from "lucide-react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { KindTag } from "./WorkshopParts";
import { PANE_IS_SHEET } from "./WorkshopSidePanel";

export function OpenBesideLabel() {
  const sheet = useMediaQuery(PANE_IS_SHEET);
  return sheet ? (
    <>
      Open
      <ArrowUpRight className="size-3.5" strokeWidth={1.9} aria-hidden="true" />
    </>
  ) : (
    <>
      Open beside
      <PanelRight className="size-3.5" strokeWidth={1.9} aria-hidden="true" />
    </>
  );
}

type Props = {
  /** The kind, only when the data really carries one (#114: documents stay untagged). */
  tag: string | null;
  /** "V2", a format, a state: mono, after the tag. */
  meta?: string | null;
  title: string;
  /** More under the title (an idea's synopsis and channels). */
  children?: ReactNode;
  /** Beside the text (an idea's reference frame). */
  aside?: ReactNode;
  onOpen?: () => void;
  /** Names what opens, for assistive tech ("Open Roadmap, Oct 2026 to Jun 2027"). */
  openLabel: string;
  loading?: boolean;
};

export default function WorkshopOutputCard({
  tag,
  meta,
  title,
  children,
  aside,
  onOpen,
  openLabel,
  loading = false,
}: Props) {
  return (
    <article className="ws-out" data-openable={onOpen ? true : undefined} aria-busy={loading || undefined}>
      <div className="ws-out-head">
        {tag ? <KindTag>{tag}</KindTag> : null}
        {meta ? <span className="ws-meta">{meta}</span> : null}
        {onOpen ? (
          <button type="button" className="ws-out-open" onClick={onOpen} aria-label={openLabel}>
            <OpenBesideLabel />
          </button>
        ) : null}
      </div>
      <div className="ws-out-body">
        <div className="ws-out-text">
          {loading ? (
            <span className="ws-skel ws-skel--title" aria-hidden="true" />
          ) : (
            <h3 className="ws-out-title">{title}</h3>
          )}
          {children}
        </div>
        {aside}
      </div>
    </article>
  );
}
